import PropTypes from 'prop-types'
import styled from 'styled-components'
import Chevron from '../static/chevron.png'
import { useField, useFormikContext } from 'formik'
import DatePicker from 'react-date-picker'
import { isValid } from 'date-fns'

/*
 * HOW TO USE IT
 *
 * <Formik
 *   initialValues={initialValues}
 *   validationSchema={validationSchema}
 *   onSubmit={(values, { setSubmitting }) => {
 *     // DO SOMETHING WITH VALUES
 *   }}
 * >
 *   {(props) => (
 *     <Form>
 *       <InlineGroup>
 *         <Datepicker
 *           type='date'
 *           name='operated_on'
 *           onChange={handleDateChange}
 *         />
 *       </InlineGroup>
 *     </Form>
 *   )}
 * </Formik>
 *
 * IMPORTANT: When using with Yup validation, you MUST add .nullable() to your date schema:
 *
 * ❌ WRONG - Will cause "Invalid Date" errors on blur:
 * opening_date: Yup.date().required('Requis')
 *
 * ✅ CORRECT - Handles empty values properly:
 * opening_date: Yup.date().nullable().required('Requis')
 *
 * This is because the Datepicker component automatically converts empty/invalid values to null,
 * and Yup needs to know that null is an acceptable value before applying the required validation.
 */

export const Datepicker = ({ onChange, noMargin, ...props }) => {
  const { setFieldValue, setFieldTouched } = useFormikContext()

  const customChange = (value) => {
    if (!isValid(value)) return

    let fieldValue = null
    if (value !== null) {
      fieldValue = new Date(value.getTime() - value.getTimezoneOffset() * 60000)
    }

    setFieldValue(props.name, fieldValue).then(() => {
      if (onChange) {
        onChange(value)
      }
    })

    setFieldTouched(props.name, true, false)
  }

  const handleBlur = () => {
    setFieldTouched(props.name, true, false)
  }

  const validateField = (value) => {
    if (!value) return undefined

    const date = new Date(value)
    const year = date.getFullYear()

    // Une année saisie sur 2 chiffres (23 au lieu de 2023) donne une date valide de l'an 23
    if (!isValid(date) || year < 1000 || year > 9999) {
      return 'Doit être au format JJ/MM/AAAA'
    }
    if (year < 1900) {
      return 'La date doit être supérieure ou égale à 1900'
    }

    return undefined
  }

  const [field, meta] = useField({
    ...props,
    validate: validateField
  })

  // react-date-picker throws on an Invalid Date, so never hand one over
  const safeValue = field.value instanceof Date && !isValid(field.value) ? null : field.value

  // An already filled-in field showing an error must say so right away, even before it is
  // touched, otherwise the submit button stays disabled with no visible reason
  const showError = Boolean(meta.error) && (meta.touched || Boolean(field.value))

  return (
    <div>
      <StyledDatepicker
        {...field}
        {...props}
        value={safeValue}
        id={props.name}
        onChange={(value) => {
          customChange(value)
        }}
        onBlur={handleBlur}
        clearIcon={null}
        format="dd/MM/y"
        calendarIcon={<CalendarIcon />}
        $noMargin={noMargin}
        className={showError ? 'error' : ''}
      />
      {showError ? <ErrorMessage $noMargin={noMargin}>{meta.error}</ErrorMessage> : null}
    </div>
  )
}

Datepicker.propTypes = {
  name: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.instanceOf(Date),
  noMargin: PropTypes.bool
}

const StyledDatepicker = styled(DatePicker)`
  div.react-date-picker__wrapper {
    font-weight: 600;
    display: block;
    width: auto;
    box-sizing: border-box;
    border: none;
    border-bottom: 1px solid #c4c4c4;
    padding: 0 0 0.4rem 0;
    margin: ${({ $noMargin: noMargin }) => (noMargin ? '0' : '0 1rem')};
  }

  div.react-date-picker__inputGroup {
    display: initial;
    padding: 0 0.5rem 0 2px;
  }

  button.react-date-picker__button {
    padding: 0;
    width: 1rem;
  }
  &.react-date-picker--disabled {
    * {
      cursor: not-allowed;
      color: #d8d8d8;
    }
    border-bottom: 1px solid #d8d8d8;
    background-color: transparent;

    div.react-date-picker__wrapper {
      border-bottom: none;
    }
  }
`

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.redText};
  font-size: 12px;
  margin-top: 4px;
  margin-left: ${({ $noMargin: noMargin }) => (noMargin ? '0' : '1rem')};
`

const CalendarIcon = styled.div`
  display: block;
  height: 1rem;
  outline: none;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.blackSecondary};
  background: url('${Chevron}');
  background-repeat: no-repeat;
  background-size: contain;
  background-position-x: right;
  background-position-y: 0.2rem;
`
